import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { CategoryType } from 'src/app/common/enums/category-type';
import { EMPTY, Subject, switchMap, takeUntil } from 'rxjs';
import { IClinicalItem } from 'src/app/pages/category/clinical-item/iclinical-item';
import { ClinicalCategoryService } from 'src/app/pages/category/clicnical-category/clinicalCategory.service';
import { ClinicalCatalogueCategory } from 'src/app/pages/category/clicnical-category/clinical-catalogue.type';
import { DiagnosisIcdDialogComponent } from 'src/app/pages/opd/opddetails/components/diagnosis-icd-dialog/diagnosis-icd-dialog.component';
import { DiagnosisSelection } from 'src/app/pages/opd/opddetails/iopddetails';
import { OPDService } from 'src/app/pages/opd/opd.service';
import { ToastrService } from 'ngx-toastr';
import { BaseRespone } from 'src/app/models/common.response';

interface CategoryWithSubcategories extends CommonDropDown {
  subcategories: SubcategoryWithItems[];
}

interface SubcategoryWithItems {
  id: number;
  name: string;
  items: IClinicalItem[];
}

export interface ClinicalCategoryPickerDialogData {
  categoryType?: CategoryType;
  existingValue?: string;
  title?: string;
  opdId?: number;
}

@Component({
  selector: 'app-suggested-investigation-popup',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule
  ],
  templateUrl: './suggested-investigation-popup.component.html',
  styleUrl: './suggested-investigation-popup.component.scss'
})
export class SuggestedInvestigationPopupComponent implements OnInit, OnDestroy {
  categories: CategoryWithSubcategories[] = [];
  selectedCategoryIndex: number = 0;
  selectedItems: Set<number> = new Set<number>(); // Store by ID for independent selection
  private _preselectedDescriptions: Set<string> = new Set<string>(); // Store initial descriptions for matching
  expandedSubcategories: Set<number> = new Set<number>();
  searchTerm = '';
  isLoading = false;

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _dialogRef: MatDialogRef<SuggestedInvestigationPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClinicalCategoryPickerDialogData,
    private _categoryService: ClinicalCategoryService,
    private _cdr: ChangeDetectorRef,
    private _translateService: TranslateService,
    private _dialog: MatDialog,
    private _opdService: OPDService,
    private _toastr: ToastrService
  ) { }

  get dialogTitle(): string {
    return this.data?.title || this._translateService.instant('LABEL.SELECT_SUGGESTED_INVESTIGATION');
  }

  get canAddDiagnosis(): boolean {
    return this.data?.categoryType === CategoryType.Diagnosis && !!this.data?.opdId;
  }

  ngOnInit(): void {
    this.parseExistingValue();
    this.loadCategories();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private loadCategories(): void {
    this.isLoading = true;
    const categoryTypeId = this.data?.categoryType ?? CategoryType.SuggestedInvestigation;
    this._categoryService.getCatalogue({ categoryTypeId })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: response => this.applyCatalogue(response.result || []),
        error: error => {
          console.error('Error loading clinical catalogue:', error);
          this.isLoading = false;
        }
      });
  }

  private applyCatalogue(catalogue: ClinicalCatalogueCategory[]): void {
    this.categories = catalogue.map(category => this.mapCategory(category));
    this.isLoading = false;
    this.selectedCategoryIndex = 0;
    this.restoreSelectedItems();
    this.categories.forEach((_, index) => this.expandFirstSubcategoryOfCategory(index));
    this._cdr.detectChanges();
  }

  private mapCategory(category: ClinicalCatalogueCategory): CategoryWithSubcategories {
    const subcategories = category.subCategories.map(subCategory => ({
      id: subCategory.id,
      name: subCategory.name || '',
      items: [...subCategory.items]
    }));

    this.addGeneralItems(subcategories, category.generalItems);
    return { id: category.id, name: category.name, subcategories };
  }

  private addGeneralItems(
    subcategories: SubcategoryWithItems[],
    generalItems: IClinicalItem[]
  ): void {
    if (generalItems.length === 0) {
      return;
    }

    const existing = subcategories.find(subCategory =>
      ['general', 'others'].includes(subCategory.name.toLowerCase())
    );
    if (existing) {
      existing.items = [...existing.items, ...generalItems];
      return;
    }

    subcategories.push({ id: -999, name: 'General', items: [...generalItems] });
  }

  private parseExistingValue(): void {
    if (!this.data?.existingValue) {
      return;
    }

    const existingText = this.data.existingValue;
    const items = existingText.split(',').map(item => item.trim()).filter(item => item.length > 0);

    items.forEach(item => {
      this._preselectedDescriptions.add(item.toLowerCase());
    });
  }

  private restoreSelectedItems(): void {
    this.categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          const itemDescription = item.description?.toLowerCase() || '';
          if (this._preselectedDescriptions.has(itemDescription)) {
            this.selectedItems.add(item.id);
          }
        });

        const hasSelected = subcategory.items.some(item =>
          this.selectedItems.has(item.id)
        );
        if (hasSelected) {
          this.expandedSubcategories.add(subcategory.id);
        }
      });
    });
  }
  private expandFirstSubcategoryOfCategory(categoryIndex: number): void {
    if (categoryIndex >= 0 && categoryIndex < this.categories.length) {
      const category = this.categories[categoryIndex];
      if (category.subcategories && category.subcategories.length > 0) {
        const firstSubcategory = category.subcategories[0];
        this.expandedSubcategories.add(firstSubcategory.id);
      }
    }
  }

  onCategoryChange(index: number): void {
    this.selectedCategoryIndex = index;
    // Always expand first subcategory when switching to any tab
    this.expandFirstSubcategoryOfCategory(index);
  }

  selectCategory(index: number): void {
    this.selectedCategoryIndex = index;
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  openAddToDiagnosis(): void {
    const opdId = this.data?.opdId;
    if (!opdId) {
      return;
    }

    this._dialog.open(DiagnosisIcdDialogComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true
    }).afterClosed().pipe(
      switchMap((selections?: DiagnosisSelection[]) => {
        const selectedItems = (selections || []).filter(selection => selection.item?.id);
        if (selectedItems.length === 0) {
          return EMPTY;
        }

        return this._opdService.addDiagnosesFromMaster({
          opdId,
          diagnosisCodeIds: [...new Set(selectedItems.map(selection => selection.item.id!))]
        });
      }),
      takeUntil(this._unsubscribeAll)
    ).subscribe({
      next: response => {
        if (!response?.isSuccess) {
          this.showBulkDiagnosisError(response);
          return;
        }

        const results = response.result ?? [];
        const added = results.filter(result => result.outcome === 'added');
        const skipped = results.filter(result => result.outcome === 'alreadyExisted');
        const rejected = results.filter(result => result.outcome === 'rejected');

        results.forEach(result => {
          if (result.clinicalItemId) {
            this.selectedItems.add(result.clinicalItemId);
          }
        });

        if (added.length > 0) {
          this._toastr.success(this._translateService.instant(
            added.length === 1
              ? 'CONSULTATION_CHECKLIST.DIAGNOSIS_ITEM_ADDED'
              : 'CONSULTATION_CHECKLIST.DIAGNOSIS_ITEMS_ADDED',
            { count: added.length }
          ));
        }

        if (skipped.length > 0) {
          this._toastr.error(this._translateService.instant(
            skipped.length === 1
              ? 'CONSULTATION_CHECKLIST.DIAGNOSIS_ITEM_SKIPPED'
              : 'CONSULTATION_CHECKLIST.DIAGNOSIS_ITEMS_SKIPPED',
            { count: skipped.length }
          ));
        }

        if (rejected.length > 0) {
          this._toastr.error(
            rejected.map(result => result.message).filter(Boolean).join('\n')
              || this._translateService.instant('CONSULTATION_CHECKLIST.ADD_DIAGNOSIS_ERROR')
          );
        }

        if (added.length + skipped.length > 0) {
          this.searchTerm = '';
          this._categoryService.invalidateCatalogue();
          this.loadCategories();
        }
      },
      error: () => this._toastr.error(
        this._translateService.instant('CONSULTATION_CHECKLIST.ADD_DIAGNOSIS_ERROR')
      )
    });
  }

  private showBulkDiagnosisError(response: BaseRespone<unknown> | undefined): void {
    const errors = response?.errors
      ? Object.values(response.errors).flat().join('\n')
      : this._translateService.instant('CONSULTATION_CHECKLIST.ADD_DIAGNOSIS_ERROR');
    this._toastr.error(errors);
  }

  onSubcategoryToggle(subcategoryId: number, isOpen: boolean): void {
    if (isOpen) {
      this.expandedSubcategories.add(subcategoryId);
    } else {
      this.expandedSubcategories.delete(subcategoryId);
    }
  }

  isSubcategoryExpanded(subcategoryId: number): boolean {
    return this.expandedSubcategories.has(subcategoryId);
  }

  onCheckboxChange(item: IClinicalItem, checked: boolean): void {
    if (checked) {
      this.selectedItems.add(item.id);
    } else {
      this.selectedItems.delete(item.id);
    }
  }

  isItemSelected(item: IClinicalItem): boolean {
    return this.selectedItems.has(item.id);
  }

  getSelectedCountForSubcategory(subcategory: SubcategoryWithItems): number {
    return subcategory.items.filter(item => this.isItemSelected(item)).length;
  }

  getSelectedCountForCategory(category: CategoryWithSubcategories): number {
    let count = 0;
    category.subcategories.forEach(subcategory => {
      count += this.getSelectedCountForSubcategory(subcategory);
    });
    return count;
  }

  getCurrentCategory(): CategoryWithSubcategories | undefined {
    return this.categories[this.selectedCategoryIndex];
  }

  getFilteredItems(subcategory: SubcategoryWithItems): IClinicalItem[] {
    const searchValue = this.searchTerm.trim().toLowerCase();

    if (!searchValue) {
      return subcategory.items;
    }

    const subcategoryName = subcategory.name?.toLowerCase() || '';
    if (subcategoryName.includes(searchValue)) {
      return subcategory.items;
    }

    return subcategory.items.filter(item =>
      (item.description || '').toLowerCase().includes(searchValue)
    );
  }

  getVisibleSubcategories(category: CategoryWithSubcategories): SubcategoryWithItems[] {
    return category.subcategories.filter(subcategory =>
      this.getFilteredItems(subcategory).length > 0
    );
  }

  hasVisibleItems(category: CategoryWithSubcategories): boolean {
    return this.getVisibleSubcategories(category).length > 0;
  }

  onSave(): void {
    // Get all selected item descriptions
    const selectedDescriptions = new Set<string>();
    this.categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          if (this.isItemSelected(item) && item.description) {
            selectedDescriptions.add(item.description);
          }
        });
      });
    });

    const result = Array.from(selectedDescriptions).join(', ');
    this._dialogRef.close(result);
  }

  onCancel(): void {
    this._dialogRef.close(null);
  }
}
